import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCompanyClaimInfoService,
  submitCompanyClaimService,
} from "../../services/CompanyRecovery/CompanyRecoveryServices";
import { searchUserServiceEmail } from "../../services/User/UsersServices";
import { PASSWORD_REGEX, PHONE_REGEX, VAT_REGEX } from "../../types/Regex";
import { primaryColor } from "../environment";
import LoadingComponent from "../LoadingPage/LoadingComponent";
import { showError } from "../Toast/Toast.functions";
import "../General.css";

const COMPANY_TYPE_OPTIONS = [
  { value: 1, labelKey: "products" },
  { value: 2, labelKey: "services" },
  { value: 3, labelKey: "productsServices" },
];

const USER_TYPE_OPTIONS = [
  { value: 1, labelKey: "AgriculturalProducer" },
  { value: 2, labelKey: "Corporation" },
];

type PageStatus = "loading" | "ready" | "error";

interface FormValues {
  [index: string]: string | number;
  accountName: string;
  accountSurname: string;
  accountPassword: string;
  userType: number;
  companyName: string;
  companyDescription: string;
  companyVat: string;
  companyPhone: string;
  companyValues: string;
  companyWebsite: string;
  companyType: number;
}

const RecoverCompany = () => {
  const { t } = useTranslation(["common", "error", "signin"]);
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [status, setStatus] = useState<PageStatus>("loading");
  const [errorCode, setErrorCode] = useState<string>("");
  const [companyEmail, setCompanyEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    getCompanyClaimInfoService(token)
      .then((res: any) => {
        const data = res.data;
        setCompanyEmail(data.email ?? "");
        formik.setValues({
          ...formik.values,
          companyName: data.name ?? "",
          companyDescription: data.description ?? "",
          companyVat: data.vat ?? "",
          companyPhone: data.phone ?? "",
          companyValues: data.values ?? "",
          companyWebsite: data.website ?? "",
          companyType: data.companyType ?? 1,
        });
        setStatus("ready");
      })
      .catch((err) => {
        setErrorCode(err?.response?.data?.code ?? "");
        setStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const formik = useFormik<FormValues>({
    initialValues: {
      accountName: "",
      accountSurname: "",
      accountPassword: "",
      userType: 1,
      companyName: "",
      companyDescription: "",
      companyVat: "",
      companyPhone: "",
      companyValues: "",
      companyWebsite: "",
      companyType: 1,
    },
    validate: (data: FormValues) => {
      const errors: Record<string, string> = {};
      if (!data.accountName) errors.accountName = t("error:nameRequired");
      if (!data.accountSurname) errors.accountSurname = t("error:surnameRequired");
      if (!data.accountPassword) {
        errors.accountPassword = t("error:passwordRequired");
      } else if (!PASSWORD_REGEX.test(data.accountPassword)) {
        errors.accountPassword = t("error:passwordInvalid");
      }
      if (!data.companyName) errors.companyName = t("error:nameRequired");
      if (!data.companyDescription) errors.companyDescription = t("error:descriptionRequired");
      if (!data.companyVat || !VAT_REGEX.test(data.companyVat)) {
        errors.companyVat = t("error:vatInvalid");
      }
      if (!data.companyPhone || !PHONE_REGEX.test(data.companyPhone)) {
        errors.companyPhone = t("error:phoneInvalid");
      }
      if (!data.companyValues) errors.companyValues = t("error:valuesRequired");
      return errors;
    },
    onSubmit: (data: FormValues) => {
      if (!token) return;
      setSubmitting(true);
      submitCompanyClaimService({
        token,
        account: {
          name: data.accountName,
          surname: data.accountSurname,
          password: data.accountPassword,
          userType: data.userType,
        },
        company: {
          name: data.companyName,
          description: data.companyDescription,
          vat: data.companyVat,
          address: null,
          logo: null,
          pictures: [],
          phone: data.companyPhone,
          values: data.companyValues,
          website: data.companyWebsite || null,
          companyType: data.companyType,
        },
      })
        .then((res: any) => {
          window.sessionStorage.setItem("accessToken", res.data.accessToken);
          window.sessionStorage.setItem("idToken", res.data.idToken);
          if (res.data.refreshToken?.token) {
            window.sessionStorage.setItem("refreshToken", res.data.refreshToken.token);
          }
          return searchUserServiceEmail({ email: companyEmail });
        })
        .then((searchResult: any) => {
          window.sessionStorage.setItem("userId", searchResult.data._id);
          window.sessionStorage.setItem("user", searchResult.data.name);
          if (
            (searchResult.data.userType === 1 || searchResult.data.userType === 2) &&
            searchResult.data.companies?.length > 0
          ) {
            window.sessionStorage.setItem("company", searchResult.data.companies[0]._id);
          }
          window.open("/", "_self");
        })
        .catch(() => {
          setSubmitting(false);
          showError(toast, t("common:genericError"));
        });
    },
  });

  const isFormFieldValid = (name: string) =>
    !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name: string) =>
    isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;

  if (status === "loading") {
    return <LoadingComponent text={t("signin:loadingUser")} />;
  }

  if (status === "error") {
    const messageKey =
      errorCode === "TokenExpired"
        ? "recoverCompanyExpired"
        : errorCode === "AlreadyClaimed"
        ? "recoverCompanyAlreadyClaimed"
        : "recoverCompanyInvalid";
    return (
      <div
        className="display-flex flex-direction-column justify-content-center align-items-center"
        style={{ height: "100vh", backgroundColor: primaryColor }}
      >
        <div className="signin-modal form-card display-flex flex-direction-column align-items-center">
          <div className="big-font margin-bottom">BIZalp</div>
          <div className="medium-font margin-bottom">{t(`common:${messageKey}`)}</div>
          {errorCode === "TokenExpired" && (
            <Button
              className="signup-button"
              label={t("common:requestNewLink")}
              onClick={() => navigate("/recoverCompany")}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="display-flex flex-direction-column justify-content-center align-items-center"
      style={{ minHeight: "100vh", backgroundColor: primaryColor, padding: "2rem 0" }}
    >
      <Toast ref={toast} />
      <div className="signin-modal form-card" style={{ maxWidth: "600px", width: "100%" }}>
        <div className="big-font margin-bottom">BIZalp</div>
        <div className="medium-font margin-bottom">{t("common:recoverCompanyTitle")}</div>

        <form onSubmit={formik.handleSubmit} className="p-fluid">
          <h6>{t("common:accountData")}</h6>
          <div className="field">
            <label>{t("common:Name")}*</label>
            <InputText
              name="accountName"
              value={formik.values.accountName}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("accountName") })}
            />
            {getFormErrorMessage("accountName")}
          </div>
          <div className="field">
            <label>{t("common:Surname")}*</label>
            <InputText
              name="accountSurname"
              value={formik.values.accountSurname}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("accountSurname") })}
            />
            {getFormErrorMessage("accountSurname")}
          </div>
          <div className="field">
            <label>Email</label>
            <InputText value={companyEmail} disabled />
          </div>
          <div className="field">
            <label>Password*</label>
            <Password
              name="accountPassword"
              value={formik.values.accountPassword}
              onChange={formik.handleChange}
              toggleMask
              feedback={false}
              inputClassName={classNames({ "p-invalid": isFormFieldValid("accountPassword") })}
            />
            {getFormErrorMessage("accountPassword")}
          </div>
          <div className="field">
            <label>{t("common:type")}*</label>
            <Dropdown
              value={formik.values.userType}
              options={USER_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(`common:${o.labelKey}`) }))}
              onChange={(e) => formik.setFieldValue("userType", e.value)}
            />
          </div>

          <h6 style={{ marginTop: "2rem" }}>{t("common:companies")}</h6>
          <div className="field">
            <label>{t("common:Name")}*</label>
            <InputText
              name="companyName"
              value={formik.values.companyName}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("companyName") })}
            />
            {getFormErrorMessage("companyName")}
          </div>
          <div className="field">
            <label>{t("common:Description")}*</label>
            <InputTextarea
              rows={3}
              name="companyDescription"
              value={formik.values.companyDescription}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("companyDescription") })}
            />
            {getFormErrorMessage("companyDescription")}
          </div>
          <div className="field">
            <label>{t("common:vat")}*</label>
            <InputText
              name="companyVat"
              value={formik.values.companyVat}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("companyVat") })}
            />
            {getFormErrorMessage("companyVat")}
          </div>
          <div className="field">
            <label>{t("common:Phone")}*</label>
            <InputText
              name="companyPhone"
              value={formik.values.companyPhone}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("companyPhone") })}
            />
            {getFormErrorMessage("companyPhone")}
          </div>
          <div className="field">
            <label>{t("common:website")}</label>
            <InputText
              name="companyWebsite"
              value={formik.values.companyWebsite}
              onChange={formik.handleChange}
            />
          </div>
          <div className="field">
            <label>{t("common:values")}*</label>
            <InputTextarea
              rows={2}
              name="companyValues"
              value={formik.values.companyValues}
              onChange={formik.handleChange}
              className={classNames({ "p-invalid": isFormFieldValid("companyValues") })}
            />
            {getFormErrorMessage("companyValues")}
          </div>
          <div className="field">
            <label>{t("common:type")}*</label>
            <Dropdown
              value={formik.values.companyType}
              options={COMPANY_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(`common:${o.labelKey}`) }))}
              onChange={(e) => formik.setFieldValue("companyType", e.value)}
            />
          </div>

          <div className="flex justify-content-center" style={{ width: "100%", marginTop: "2rem" }}>
            <Button
              type="submit"
              className="signup-button"
              style={{ width: "50%" }}
              label={t("common:recoverCompanySubmit")}
              disabled={submitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecoverCompany;
